import React, { useState, useRef } from 'react';
import { Camera, CheckCircle, AlertTriangle, Loader, RefreshCw, RotateCcw } from 'lucide-react';
import { isNativePlatform } from '../utils/platform';
import { getCurrentPosition } from '../utils/geolocation';

interface PhotoVerificationProps {
  rbtName: string;
  onPhotoCapture: (photoDataUrl: string, location: { latitude: number; longitude: number }) => Promise<void>;
  disabled?: boolean;
}

export const PhotoVerification: React.FC<PhotoVerificationProps> = ({
  rbtName,
  onPhotoCapture,
  disabled = false,
}) => {
  const [captureStatus, setCaptureStatus] = useState<'idle' | 'capturing' | 'success' | 'error'>('idle');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  const startCamera = async (mode: 'user' | 'environment' = facingMode) => {
    setCaptureStatus('capturing');
    setError(null);

    // Stop existing stream first
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 720 },
          height: { ideal: 1280 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(console.error);
        };
        setStream(mediaStream);
        setFacingMode(mode);
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      setCaptureStatus('error');

      let errorMessage = 'Failed to access camera. ';

      if (err.name === 'NotAllowedError') {
        errorMessage += 'Please enable camera permissions in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'Camera is already in use by another application.';
      } else {
        errorMessage += err.message || 'Please try again.';
      }

      setError(errorMessage);
    }
  };

  const switchCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    startCamera(newMode);
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCaptureStatus('idle');
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    context.fillStyle = 'rgba(0, 0, 0, 0.7)';
    context.fillRect(0, canvas.height - 60, canvas.width, 60);

    context.fillStyle = 'white';
    context.font = '20px Arial';
    context.fillText(
      `${rbtName} - ${new Date().toLocaleString()}`,
      20,
      canvas.height - 25
    );

    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setPhotoDataUrl(dataUrl);

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCaptureStatus('idle');
  };

  // Draw the same name + timestamp compliance bar the web capture applies,
  // onto an already-captured image (used for the native camera result).
  const stampImage = (srcDataUrl: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(srcDataUrl);
          return;
        }
        context.drawImage(img, 0, 0);
        context.fillStyle = 'rgba(0, 0, 0, 0.7)';
        context.fillRect(0, canvas.height - 60, canvas.width, 60);
        context.fillStyle = 'white';
        context.font = '20px Arial';
        context.fillText(`${rbtName} - ${new Date().toLocaleString()}`, 20, canvas.height - 25);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => reject(new Error('Failed to process photo'));
      img.src = srcDataUrl;
    });

  // Native (Capacitor) camera: opens the OS camera UI and returns a data URL.
  // Replaces the getUserMedia live-preview flow, which is unreliable in a WebView.
  const captureNativePhoto = async () => {
    setCaptureStatus('capturing');
    setError(null);
    try {
      const { Camera: CapCamera, CameraResultType, CameraSource, CameraDirection } = await import(
        '@capacitor/camera'
      );
      const photo = await CapCamera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        direction: CameraDirection.Front,
        width: 720,
        height: 1280,
      });

      if (!photo.dataUrl) {
        throw new Error('No photo captured');
      }
      const stamped = await stampImage(photo.dataUrl);
      setPhotoDataUrl(stamped);
      setCaptureStatus('idle');
    } catch (err: any) {
      // Capacitor throws a "cancelled" error when the user backs out — not a failure.
      if (String(err?.message || '').toLowerCase().includes('cancel')) {
        setCaptureStatus('idle');
        return;
      }
      console.error('Native camera error:', err);
      setCaptureStatus('error');
      setError('Failed to access camera. ' + (err?.message || 'Please try again.'));
    }
  };

  const retakePhoto = () => {
    setPhotoDataUrl(null);
    setCaptureStatus('idle');
    setError(null);
  };

  const submitPhoto = async () => {
    if (!photoDataUrl) return;

    setUploading(true);
    setError(null);

    try {
      // On web, pre-check browser geolocation availability/permission for a
      // friendlier prompt. On native, getCurrentPosition() handles the OS
      // permission flow itself, so skip these browser-only checks.
      if (!isNativePlatform()) {
        if (!navigator.geolocation) {
          throw new Error('Geolocation is not supported by your browser. Please use a different browser.');
        }

        // Check permission state first (if available)
        let permissionState: PermissionState | null = null;
        try {
          if (navigator.permissions) {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            permissionState = permission.state;
          }
        } catch {
          // permissions API not available, continue anyway
        }

        // If permission is denied, show helpful message immediately
        if (permissionState === 'denied') {
          // Mirror GeolocationPositionError shape (code 1 = PERMISSION_DENIED) so the catch handler below reports it correctly
          const permissionError: any = new Error('Location permission denied');
          permissionError.code = 1;
          throw permissionError;
        }
      }

      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 30000, // Increased timeout for permission prompt
        maximumAge: 0,
      });

      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      await onPhotoCapture(photoDataUrl, location);
      setCaptureStatus('success');
    } catch (err: any) {
      console.error('Photo submission error:', err);

      let errorMessage = 'Failed to submit photo. ';

      // Handle geolocation-specific errors
      if (err.code === 1) {
        errorMessage = 'Location permission denied. Please go to your browser settings, allow location access for this site, then tap "Confirm & Continue" again.';
      } else if (err.code === 2) {
        errorMessage = 'Unable to determine your location. Please ensure location services are enabled on your device and try again.';
      } else if (err.code === 3) {
        errorMessage = 'Location request timed out. Please ensure you have a good GPS signal and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setCaptureStatus('idle'); // Keep photo, allow retry
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Inline styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '16px',
    },
    headerBox: {
      backgroundColor: '#f5f3ff',
      border: '1px solid #ddd6fe',
      borderRadius: '8px',
      padding: '16px',
    },
    headerContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    headerIcon: {
      width: '24px',
      height: '24px',
      color: '#7c3aed',
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#4c1d95',
      margin: 0,
    },
    headerSubtitle: {
      fontSize: '14px',
      color: '#5b21b6',
      marginTop: '4px',
    },
    headerHint: {
      fontSize: '12px',
      color: '#6d28d9',
      marginTop: '8px',
    },
    statusBox: {
      border: '1px solid #e5e0db',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#f9fafb',
    },
    statusBoxCapturing: {
      border: '1px solid #3b82f6',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#eff6ff',
    },
    statusBoxSuccess: {
      border: '1px solid #22c55e',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#f0fdf4',
    },
    statusBoxError: {
      border: '1px solid #ef4444',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#fef2f2',
    },
    statusContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    statusIcon: {
      width: '20px',
      height: '20px',
    },
    statusText: {
      fontSize: '14px',
      fontWeight: 500,
    },
    errorText: {
      marginTop: '12px',
      fontSize: '14px',
      color: '#ef4444',
    },
    cameraContainer: {
      backgroundColor: '#1f2937',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative' as const,
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      aspectRatio: '3/4',
    },
    video: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
    capturedImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
    switchCameraButton: {
      position: 'absolute' as const,
      top: '12px',
      right: '12px',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '44px',
      height: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 10,
    },
    placeholder: {
      width: '100%',
      height: '100%',
      minHeight: '300px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#9ca3af',
    },
    placeholderContent: {
      textAlign: 'center' as const,
    },
    placeholderIcon: {
      width: '64px',
      height: '64px',
      margin: '0 auto 8px',
      opacity: 0.5,
    },
    placeholderText: {
      fontSize: '14px',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },
    buttonRow: {
      display: 'flex',
      gap: '8px',
    },
    primaryButton: {
      flex: 1,
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s',
    },
    successButton: {
      flex: 1,
      padding: '12px 24px',
      backgroundColor: '#22c55e',
      color: '#ffffff',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s',
    },
    secondaryButton: {
      padding: '12px 24px',
      backgroundColor: '#e5e0db',
      color: '#374151',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    successAlert: {
      backgroundColor: '#f0fdf4',
      border: '1px solid #86efac',
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center' as const,
      color: '#166534',
    },
    infoList: {
      fontSize: '12px',
      color: '#6b6b6b',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    infoItem: {
      marginBottom: '4px',
    },
  };

  const getStatusStyles = () => {
    switch (captureStatus) {
      case 'success':
        return styles.statusBoxSuccess;
      case 'error':
        return styles.statusBoxError;
      case 'capturing':
        return styles.statusBoxCapturing;
      default:
        return styles.statusBox;
    }
  };

  const getStatusIcon = () => {
    switch (captureStatus) {
      case 'capturing':
        return <Camera style={{ ...styles.statusIcon, color: '#3b82f6' }} />;
      case 'success':
        return <CheckCircle style={{ ...styles.statusIcon, color: '#22c55e' }} />;
      case 'error':
        return <AlertTriangle style={{ ...styles.statusIcon, color: '#ef4444' }} />;
      default:
        return <Camera style={{ ...styles.statusIcon, color: '#6b6b6b' }} />;
    }
  };

  const getStatusText = () => {
    switch (captureStatus) {
      case 'capturing':
        return 'Camera active - Position yourself in frame';
      case 'success':
        return 'Photo captured and saved successfully!';
      case 'error':
        return 'Camera access failed';
      default:
        return photoDataUrl ? 'Photo captured - Confirm to continue' : 'Ready to capture photo';
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerBox}>
        <div style={styles.headerContent}>
          <Camera style={styles.headerIcon} />
          <div>
            <h3 style={styles.headerTitle}>Photo Verification</h3>
            <p style={styles.headerSubtitle}>
              Take a photo to verify your identity and location
            </p>
            <p style={styles.headerHint}>
              Your photo will include a timestamp and be stored for compliance
            </p>
          </div>
        </div>
      </div>

      {/* Status Display */}
      <div style={getStatusStyles()}>
        <div style={styles.statusContent}>
          {getStatusIcon()}
          <span style={styles.statusText}>{getStatusText()}</span>
        </div>

        {error && <div style={styles.errorText}>{error}</div>}
      </div>

      {/* Camera/Photo Display */}
      <div style={styles.cameraContainer}>
        {/* Switch Camera Button */}
        {captureStatus === 'capturing' && (
          <button
            onClick={switchCamera}
            style={styles.switchCameraButton}
            title="Switch Camera"
          >
            <RotateCcw size={22} />
          </button>
        )}

        {/* Video element - always in DOM */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            ...styles.video,
            display: captureStatus === 'capturing' ? 'block' : 'none',
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          }}
        />

        {/* Show captured photo */}
        {photoDataUrl && captureStatus !== 'capturing' && (
          <img
            src={photoDataUrl}
            alt="Captured verification"
            style={styles.capturedImage}
          />
        )}

        {/* Placeholder when no camera and no photo */}
        {captureStatus !== 'capturing' && !photoDataUrl && (
          <div style={styles.placeholder}>
            <div style={styles.placeholderContent}>
              <Camera style={styles.placeholderIcon} />
              <p style={styles.placeholderText}>Camera preview will appear here</p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Action Buttons */}
      <div style={styles.buttonContainer}>
        {/* Start Camera button - show when idle with no photo */}
        {captureStatus === 'idle' && !photoDataUrl && (
          <button
            onClick={() => (isNativePlatform() ? captureNativePhoto() : startCamera())}
            disabled={disabled || uploading}
            style={{
              ...styles.primaryButton,
              ...(disabled || uploading ? styles.disabledButton : {}),
            }}
            onMouseOver={(e) => {
              if (!disabled && !uploading) e.currentTarget.style.backgroundColor = '#4F6367';
            }}
            onMouseOut={(e) => {
              if (!disabled && !uploading) e.currentTarget.style.backgroundColor = '#3b82f6';
            }}
          >
            <Camera style={{ width: '20px', height: '20px' }} />
            Start Camera
          </button>
        )}

        {/* Capture Photo button - show when camera is active */}
        {captureStatus === 'capturing' && (
          <div style={styles.buttonRow}>
            <button
              onClick={capturePhoto}
              style={styles.primaryButton}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4F6367')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#3b82f6')}
            >
              <Camera style={{ width: '20px', height: '20px' }} />
              Capture Photo
            </button>
            <button
              onClick={stopCamera}
              style={styles.secondaryButton}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#d1d5db')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#e5e0db')}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Confirm/Retake buttons - show when photo is captured but not yet submitted */}
        {photoDataUrl && captureStatus !== 'success' && (
          <div style={styles.buttonRow}>
            <button
              onClick={submitPhoto}
              disabled={uploading}
              style={{
                ...styles.successButton,
                ...(uploading ? styles.disabledButton : {}),
              }}
              onMouseOver={(e) => {
                if (!uploading) e.currentTarget.style.backgroundColor = '#16a34a';
              }}
              onMouseOut={(e) => {
                if (!uploading) e.currentTarget.style.backgroundColor = '#22c55e';
              }}
            >
              {uploading ? (
                <>
                  <Loader style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }} />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle style={{ width: '20px', height: '20px' }} />
                  Confirm & Continue
                </>
              )}
            </button>
            <button
              onClick={retakePhoto}
              disabled={uploading}
              style={{
                ...styles.secondaryButton,
                ...(uploading ? styles.disabledButton : {}),
              }}
              onMouseOver={(e) => {
                if (!uploading) e.currentTarget.style.backgroundColor = '#d1d5db';
              }}
              onMouseOut={(e) => {
                if (!uploading) e.currentTarget.style.backgroundColor = '#e5e0db';
              }}
            >
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              Retake
            </button>
          </div>
        )}

        {/* Success state - show continue info */}
        {captureStatus === 'success' && (
          <div style={styles.successAlert}>
            Photo verification complete! Proceeding to next step...
          </div>
        )}
      </div>

      {/* Info Text */}
      <ul style={styles.infoList}>
        <li style={styles.infoItem}>• Photo will include your name and timestamp</li>
        <li style={styles.infoItem}>• High-resolution photo required for verification</li>
        <li style={styles.infoItem}>• Photo and location data stored for HIPAA compliance</li>
      </ul>
    </div>
  );
};
